import { Injectable, ɵConsole } from '@angular/core';
import { Actions, createEffect, Effect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of, Subscription } from 'rxjs';
import { map, tap, withLatestFrom, switchMap, mapTo, concatMap } from 'rxjs/operators';
import { CourseService } from '../../_services/course.service';
import * as actions from './course-player.actions';
import { Howl } from 'howler';
import * as fromCoursePlayer from './course-player.reducer';

import { WINDOW } from '../../_helpers/window-provider';
import { Inject } from '@angular/core';
import { Router } from '@angular/router';
import { MixpanelService } from '@app/_services/mixpanel.service';

@Injectable()
export class CoursePlayerEffects {
    
  coursePlayerState$: Observable<any>  
  audio: Howl = new Howl({
    src: ["https://www.fsi-language-courses.net/file.mp3"],
    autoplay: false,
    preload: true,      
    html5: true
  });    


  constructor(
    @Inject(WINDOW) private window: Window,
    private actions$: Actions,
    private router: Router,
    private courseService: CourseService,
    private mixpanel: MixpanelService,
    private store: Store<{ coursePlayerState: fromCoursePlayer.State }>,
    
  ) {
    this.coursePlayerState$ = this.store.select(fromCoursePlayer.getCoursePlayerState);
    this.audio.context = this;
  }
    
  isPlaying = function(currentSoundId) {    
    let self = this;
    setInterval(function(self) {      
      self.context.store.dispatch(new actions.AudioScrubInterval(self.seek()));
    }, 1000, self)
  }
  
  @Effect()
  public audioScrubInterval$ = this.actions$.pipe(
    ofType(actions.AUDIO_SCRUB_INTERVAL),
    map( action => action ),
    concatMap( payload => [
      new actions.AudioScrubUpdate(payload)
    ]));   


  @Effect({dispatch: false})
  public skippedForward$ = this.actions$.pipe(
    ofType(actions.SKIP_FORWARD),
    withLatestFrom(this.store.select(fromCoursePlayer.getCoursePlayerState)),
    switchMap(([action, state]) => {
              
      this.mixpanel.track('Course Player - Seek Audio Forward');
    
      this.audio.seek(this.audio.seek()+10);

      this.store.dispatch(new actions.AudioScrubUpdate(this.audio.seek()))

      return of([]);
    }));

    @Effect({dispatch: false})
    public skippedBackwards$ = this.actions$.pipe(
      ofType(actions.SKIP_BACK),      
      withLatestFrom(this.store.select(fromCoursePlayer.getCoursePlayerState)),
      switchMap(([action, state]) => {
              
        this.mixpanel.track('Course Player - Seek Audio Backwards');

        this.audio.seek(this.audio.seek()-10);

        this.store.dispatch(new actions.AudioScrubUpdate(this.audio.seek()))

        return of([]);
      }));


  @Effect({dispatch: false})
  close$ = this.actions$.pipe(
    ofType(actions.CLOSE),
    withLatestFrom(this.store.select(fromCoursePlayer.getCoursePlayerState)),
    map(([action, state]) => {

      this.mixpanel.track('Course Player - Close Player');
              
      var newState = JSON.parse(JSON.stringify(state));        
      newState.ui.content.currentAudioIndex = this.audio.seek();        
      
      this.audio.pause();

      if (newState.ids.length > 0) {
        this.courseService.recordProgress(newState.courseId, newState)        
      }
      
      this.router.navigate(["/account"]);//.navigateByUrl("/account");      
    }));


  @Effect({dispatch: false})
  load$ = this.actions$.pipe(
    ofType(actions.LOAD),
    withLatestFrom(this.store.select(fromCoursePlayer.getCoursePlayerState)),
    switchMap(([action, state]) => {
               
      let courseData = this.courseService.getCourseData().courseData;
      let entity = state.entities[state.selectedId];      

      let unit = courseData.entities.units[entity.unitId];

      this.mixpanel.track('Course Player - Load Player', { unit: unit, entity: entity });

      this.audio = new Howl({
        src: [unit.audio],
        autoplay: false,
        preload: true,
        html5: true,
        onplay: this.isPlaying
      });

      // Attach context for isPlaying
      this.audio.context = this;

      return of([]);
    }));


  @Effect({dispatch: false})
  loadProgress$ = this.actions$.pipe(
    ofType(actions.LOAD_PROGRESS),
    withLatestFrom(this.store.select(fromCoursePlayer.getCoursePlayerState)),
    switchMap(([action, state]) => {
          
      let courseData = this.courseService.getCourseData().courseData;
      let entity = state.entities[state.selectedId];      

      let unit = courseData.entities.units[entity.unitId];

      this.mixpanel.track('Course Player - Load Player - Progress', { unit: unit, entity: entity });

      this.audio = new Howl({
        src: [unit.audio],
        autoplay: false,
        preload: true,
        html5: true,
        onplay: this.isPlaying
      });

      // Attach context for isPlaying
      this.audio.context = this;

      if (state.ui.content.currentAudioIndex > entity.audioIndex) {
        this.audio.seek(state.ui.content.currentAudioIndex);
      } else {
        this.audio.seek(entity.audioIndex);
      }

      return of([]);
    }));
  

  @Effect({dispatch: false})
  pagedRight$ = this.actions$.pipe(
    ofType(actions.PAGE_RIGHT),
    withLatestFrom(this.store.select(fromCoursePlayer.getCoursePlayerState)),
    switchMap(([action, state]) => {
                 
      var entity = state.entities[state.selectedId];

      this.mixpanel.track('Course Player - Page Right', { entity: entity });
            
      this.audio.stop();      
      this.audio.seek(entity.audioIndex);
      this.window.scrollTo(0,0);   
      
      return of([]);
    }));

  @Effect({dispatch: false})
  pagedLeft$ = this.actions$.pipe(
    ofType(actions.PAGE_LEFT),
    withLatestFrom(this.store.select(fromCoursePlayer.getCoursePlayerState)),
    switchMap(([action, state]) => {          

      var entity = state.entities[state.selectedId];            

      this.mixpanel.track('Course Player - Page Left', { entity: entity });

      this.audio.stop();
      this.audio.seek(entity.audioIndex);      
      this.window.scrollTo(0,0);   

      return of([]);
    }));
 
  @Effect({dispatch: false})
  public play$: Observable<Action> = this.actions$.pipe(
    ofType(actions.PLAY),
    tap( () => {            

      this.mixpanel.track('Course Player - Play Audio Button');

      if (!this.audio.playing()) {
        this.audio.play();
      }
    }));

  @Effect({dispatch: false})
  public pause$: Observable<Action> = this.actions$.pipe(
    ofType(actions.PAUSE),
    tap( () => {

      this.mixpanel.track('Course Player - Pause Audio Button');
      
      if (this.audio.playing()) {
        this.audio.pause();
      }
    }));
      
}